// Shared three-link header nav with a hamburger panel below the `sm:` breakpoint.
// Used by Calendar, Members, and Settings pages.
import { useEffect, useRef, useState } from "react";

type Section = "calendar" | "members" | "settings";

const LINKS: ReadonlyArray<{ section: Section; href: string; label: string }> = [
  { section: "calendar", href: "/calendar", label: "Calendar" },
  { section: "members", href: "/members", label: "Members" },
  { section: "settings", href: "/settings", label: "Settings" },
];

const PANEL_ID = "primary-nav-panel";

export function HeaderNav({ active }: { active: Section }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onClickOutside = (e: MouseEvent) => {
      const node = containerRef.current;
      if (node && e.target instanceof Node && !node.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClickOutside);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClickOutside);
    };
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      {/* Inline links at sm: and above. Unchanged from the original three-link layout. */}
      <nav className="hidden sm:flex gap-4 text-sm" aria-label="Primary">
        {LINKS.map((l) => (
          <a
            key={l.section}
            href={l.href}
            className={
              l.section === active
                ? "font-medium text-stone-900"
                : "text-stone-600 hover:text-stone-900"
            }
          >
            {l.label}
          </a>
        ))}
      </nav>

      {/* Hamburger button below sm:. ≥44×44 tap target, focus ring, aria-expanded. */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={PANEL_ID}
        aria-label={open ? "Close menu" : "Open menu"}
        className="sm:hidden inline-flex items-center justify-center min-h-[44px] min-w-[44px] rounded-lg text-stone-700 hover:bg-stone-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-900"
      >
        {open ? <CloseIcon /> : <MenuIcon />}
      </button>

      {/* Dropdown panel below sm:. Closes on link click via onClick. */}
      {open && (
        <div
          id={PANEL_ID}
          className="sm:hidden absolute right-0 top-full mt-2 w-44 rounded-xl border border-stone-200 bg-white shadow-lg z-50 overflow-hidden"
          role="menu"
        >
          <ul className="py-1">
            {LINKS.map((l) => (
              <li key={l.section}>
                <a
                  href={l.href}
                  role="menuitem"
                  onClick={() => setOpen(false)}
                  className={`block px-4 py-3 min-h-[44px] text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-stone-900 ${
                    l.section === active
                      ? "font-medium text-stone-900 bg-stone-50"
                      : "text-stone-700 hover:bg-stone-50"
                  }`}
                >
                  {l.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function MenuIcon() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="4" y1="7" x2="20" y2="7" />
      <line x1="4" y1="12" x2="20" y2="12" />
      <line x1="4" y1="17" x2="20" y2="17" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="6" y1="6" x2="18" y2="18" />
      <line x1="18" y1="6" x2="6" y2="18" />
    </svg>
  );
}
